import React, { type ReactElement, useEffect, useState } from 'react';
import { faPersonCircleXmark } from '@fortawesome/free-solid-svg-icons';
import TableActions from 'atom-components/table-actions-lite';
import ConfirmDeallocateMarkerModal from 'pages/marking/components/confirm-deallocate-marker-modal';
import DeallocationResultsModal from 'pages/marking/components/deallocation-results-modal';
import { isAnActiveMarkerOfResponse } from 'pages/marking/helpers/marker.helpers';
import { marksOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import type { ChangeMarkerMark } from 'pages/marking/types';
import MarkingTaskPolicy from 'policies/marking-task.policy';
import type { PartialOrganisationUserWithPartialUser } from 'services/assist-api/entities/customer-user/types';
import { useTestInstanceResponseMarks } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import { usePageAccess } from 'services/providers/page-access.provider';

type Props = {
  responseId: number;
  allocatedMarkerId: number;
  allocatedOrganisationUser: PartialOrganisationUserWithPartialUser | undefined;
  markId?: number;
  onDeallocate?: () => void;
};

export default function DeallocateMarkerButton({
  responseId,
  allocatedMarkerId,
  allocatedOrganisationUser,
  markId,
  onDeallocate
}: Props): ReactElement {
  const [isDeterminingWhichModal, setIsDeterminingWhichModal] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState<boolean>(false);
  const { authorisableUser } = usePageAccess();

  // Fetch marks and mark items linked to the response to determine if we can deallocate the marker.
  const marksQuery = useTestInstanceResponseMarks<ChangeMarkerMark>(
    marksOfResponseParams(responseId),
    // Only run this query when the Deallocate Marker button is clicked for performance reasons:
    { enabled: isDeterminingWhichModal }
  );

  useEffect(() => {
    if (isDeterminingWhichModal && marksQuery.isSuccess) {
      if (isAnActiveMarkerOfResponse(allocatedMarkerId, marksQuery.data?.json)) {
        setIsResultsModalOpen(true);
      } else {
        setIsConfirmModalOpen(true);
      }
      setIsDeterminingWhichModal(false);
    }
  }, [allocatedMarkerId, isDeterminingWhichModal, marksQuery]);

  return (
    <>
      <ConfirmDeallocateMarkerModal
        isOpen={isConfirmModalOpen}
        onClose={(): void => setIsConfirmModalOpen(false)}
        responseId={responseId}
        allocatedMarkerId={allocatedMarkerId}
        allocatedOrganisationUser={allocatedOrganisationUser}
        onDeallocate={onDeallocate}
      />
      <DeallocationResultsModal
        isOpen={isResultsModalOpen}
        onClose={(): void => setIsResultsModalOpen(false)}
        responseId={responseId}
        allocatedMarkerId={allocatedMarkerId}
        allocatedOrganisationUser={allocatedOrganisationUser}
        onForceReallocate={(): void => (onDeallocate ? onDeallocate() : undefined)}
      />
      {MarkingTaskPolicy.administrate(authorisableUser) && (
        <TableActions.Button
          tooltip={
            markId ? 'Cannot Deallocate Marker (marking already started)' : 'Deallocate Marker'
          }
          onClick={(): void => setIsDeterminingWhichModal(true)}
          icon={faPersonCircleXmark}
          // If the marking task has a mark ID, we can assume the marker of the marking task has
          // started it and therefore the marker shouldn't be able to be deallocated without first
          // being removed as the active marker. In this scenario is it safe to assume that the
          // button can be disabled.
          //
          // More detailed checks are done after the button is clicked (checking other marks and
          // mark items linked to the response) for performance reasons. We don't want to disable
          // the button in this more complex scenario; instead the marking admin is presented with
          // the Deallocation Results modal with a breakdown of what needs to be changed before
          // deallocation can proceed.
          disabled={isDeterminingWhichModal || markId !== undefined}
        />
      )}
    </>
  );
}
